<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <template v-else-if="!isAdmin">
            <div class="camp-denied">
              Esta sección es exclusiva del super administrador de Somos Teatro.
            </div>
          </template>

          <template v-else>
            <div class="camp-toolbar">
              <div class="camp-title">
                <h1>PREVENTAS</h1>
                <h2>GESTOR DE CAMPAÑAS</h2>
              </div>
              <button type="button" class="camp-primary-btn" @click="goToNew">
                CREAR NUEVA CAMPAÑA
              </button>
            </div>

            <div class="camp-panel">
              <div class="camp-panel-header">
                <v-icon size="22" color="#ffffff">mdi-email-fast-outline</v-icon>
                <span>CAMPAÑAS</span>
              </div>

              <div class="camp-panel-body">
                <div class="camp-table-scroll">
                  <table class="camp-table">
                    <thead>
                      <tr>
                        <th>NOMBRE CAMPAÑA</th>
                        <th>OBRAS</th>
                        <th>CLIENTES</th>
                        <th>FECHA ENVÍO</th>
                        <th>RESULTADOS</th>
                        <th>ESTADO</th>
                        <th class="camp-col-actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in campaigns" :key="c.id">
                        <td class="camp-cell-name">{{ c.name }}</td>
                        <td class="camp-cell-plays">{{ playsLabel(c) }}</td>
                        <td>{{ c.total_recipients }}</td>
                        <td>{{ c.send_date_display }}</td>
                        <td class="camp-cell-results">{{ c.results_display }}</td>
                        <td>
                          <span class="camp-status" :class="statusClass(c.status)">
                            {{ c.status_display.toUpperCase() }}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            class="camp-row-btn"
                            @click="openCampaign(c)"
                          >
                            {{ c.is_editable ? 'REVISAR' : 'DETALLES' }}
                          </button>
                        </td>
                      </tr>
                      <tr v-if="campaigns.length === 0">
                        <td colspan="7" class="camp-empty">
                          Todavía no hay campañas creadas.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </template>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchCampaigns } from "@/services/teatroApi";

export default {
  name: "CampaignList",
  data() {
    return {
      campaigns: [],
      loading: true,
      error: "",
    };
  },
  computed: {
    isAdmin() {
      return this.$store?.state?.is_admin === true;
    },
  },
  async created() {
    await this.loadAuth();
    if (this.isAdmin) {
      await this.load();
    } else {
      this.loading = false;
    }
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.campaigns = (await fetchCampaigns()) || [];
      } catch (e) {
        this.error = "No se pudieron cargar las campañas.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    // Muestra hasta dos títulos y resume el resto con "…" (como el mockup).
    playsLabel(campaign) {
      const titles = (campaign.plays || []).map((p) => p.title);
      if (!titles.length) return "-";
      if (titles.length <= 2) return titles.join(", ");
      return `${titles.slice(0, 2).join(", ")} …`;
    },
    statusClass(status) {
      if (status === "sent") return "camp-status-sent";
      if (status === "failed") return "camp-status-failed";
      if (status === "sending") return "camp-status-sending";
      return "camp-status-pending";
    },
    goToNew() {
      window.location.href = "/preventa/nueva/";
    },
    // Una campaña ya PROGRAMADA se revisa en su ficha (/preventa/<id>/), que es
    // donde se ve el correo que va a salir y cuándo; el asistente queda a un
    // clic desde ahí con "REVISAR CAMPAÑA". Las demás siguen abriendo el
    // asistente, o la ficha si ya no se pueden editar.
    openCampaign(campaign) {
      if (campaign.status === "scheduled" || !campaign.is_editable) {
        this.goToDetail(campaign);
      } else {
        this.goToEdit(campaign);
      }
    },
    goToEdit(campaign) {
      window.location.href = `/preventa/${campaign.id}/editar/`;
    },
    goToDetail(campaign) {
      window.location.href = `/preventa/${campaign.id}/`;
    },
  },
};
</script>

<style scoped>
.camp-toolbar {
  max-width: 1080px;
  margin: 0 auto 22px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
}

.camp-title h1 {
  margin: 0;
  font-family: var(--font-family-heading);
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 1.6px;
  color: var(--color-theater-text);
}

.camp-title h2 {
  margin: 2px 0 0;
  font-family: var(--font-family-heading);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1.4px;
  color: #e2334c;
}

.camp-primary-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 14px 30px;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.camp-primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(200, 16, 46, 0.45);
}

.camp-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.camp-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.camp-panel-body {
  padding: 28px 34px 30px;
}

.camp-table-scroll {
  overflow-x: auto;
}

.camp-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
}

.camp-table th {
  text-align: left;
  padding: 8px 14px 18px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.camp-table td {
  padding: 15px 14px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  vertical-align: middle;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.camp-table tbody tr:first-child td {
  border-top: none;
}

.camp-cell-name {
  text-transform: uppercase;
}

.camp-cell-plays,
.camp-cell-results {
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  max-width: 230px;
}

.camp-col-actions {
  width: 130px;
}

.camp-status {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  white-space: nowrap;
}

.camp-status-sent {
  background: rgba(46, 160, 87, 0.18);
  color: #66d38a;
}

.camp-status-pending {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.75);
}

.camp-status-sending {
  background: rgba(255, 190, 60, 0.18);
  color: #ffca5f;
}

.camp-status-failed {
  background: rgba(200, 16, 46, 0.22);
  color: #ff8497;
}

.camp-row-btn {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 20px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.camp-row-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.4);
}

.camp-empty {
  text-align: center;
  padding: 30px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

.camp-denied {
  max-width: 1080px;
  margin: 0 auto;
  padding: 40px;
  text-align: center;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-weight: 600;
  letter-spacing: 0.6px;
}

@media (max-width: 700px) {
  .camp-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .camp-panel-body {
    padding: 22px 16px 24px;
  }
}
</style>
